import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  IconButton,
  Box,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Typography,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon
} from '@mui/icons-material';
import npcSpritesData from '../../data/npc-sprites.json';

const SPEAKER_SIDES = [0, 1]; // 0 = left, 1 = right
const READ_SPEEDS = ['Default', 'Slow', 'Fast'];

const ConversationEditDialog = ({ open, conversation, npcName, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    assetName: '',
    npcName: '',
    sentences: [],
    isActive: true
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Get available sprites for the current NPC
  const availableSprites = formData.npcName ? (npcSpritesData[formData.npcName] || []) : [];
  const allSprites = Object.values(npcSpritesData).flat();

  useEffect(() => {
    if (open && conversation) {
      setFormData({
        assetName: conversation.assetName || '',
        npcName: conversation.npcName || npcName || '',
        sentences: parseJSON(conversation.sentences),
        isActive: conversation.isActive !== undefined ? conversation.isActive : true
      });
    } else if (open && !conversation) {
      setFormData({
        assetName: '',
        npcName: npcName || '',
        sentences: [],
        isActive: true
      });
    }
    setError(null);
  }, [open, conversation, npcName]);

  const parseJSON = (jsonString) => {
    try {
      return JSON.parse(jsonString);
    } catch {
      return [];
    }
  };

  const handleSave = async () => {
    if (!formData.assetName.trim()) {
      setError('Asset name is required');
      return;
    }
    if (!formData.npcName.trim()) {
      setError('NPC name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(formData);
      onClose();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save conversation');
    } finally {
      setSaving(false);
    }
  };

  const addSentence = () => {
    setFormData({
      ...formData,
      sentences: [
        ...formData.sentences,
        {
          speakerName: '',
          text: '',
          speakerSpriteName: '',
          speakerSide: 0,
          readSpeed: 'Default',
          textMobileOverride: null
        }
      ]
    });
  };

  const updateSentence = (index, field, value) => {
    const newSentences = [...formData.sentences];
    newSentences[index] = { ...newSentences[index], [field]: value };
    setFormData({ ...formData, sentences: newSentences });
  };

  const deleteSentence = (index) => {
    setFormData({
      ...formData,
      sentences: formData.sentences.filter((_, i) => i !== index)
    });
  };

  const moveSentence = (index, direction) => {
    const newSentences = [...formData.sentences];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newSentences.length) return;
    [newSentences[index], newSentences[newIndex]] = [newSentences[newIndex], newSentences[index]];
    setFormData({ ...formData, sentences: newSentences });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{conversation ? 'Edit Conversation' : 'Create New Conversation'}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {conversation && (
            <Box>
              <Typography variant="caption" color="text.secondary">Conversation ID</Typography>
              <Typography variant="body2" fontFamily="monospace">{conversation.id}</Typography>
            </Box>
          )}

          <TextField
            label="Asset Name"
            value={formData.assetName}
            onChange={(e) => setFormData({ ...formData, assetName: e.target.value })}
            fullWidth
            required
            helperText="Human-readable identifier (e.g., WallyGreeting1, AgentDecline)"
          />

          <TextField
            label="NPC Name"
            value={formData.npcName}
            onChange={(e) => setFormData({ ...formData, npcName: e.target.value })}
            fullWidth
            required
            disabled={!!npcName}
            helperText="NPC this conversation belongs to"
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              />
            }
            label="Active"
          />

          <Divider />

          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Sentences ({formData.sentences.length})</Typography>
            <Button startIcon={<AddIcon />} onClick={addSentence} size="small">
              Add Sentence
            </Button>
          </Stack>

          {formData.sentences.map((sentence, index) => (
            <Paper key={index} sx={{ p: 2, bgcolor: 'background.default' }}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle2">Sentence #{index + 1}</Typography>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => moveSentence(index, 'up')}
                      disabled={index === 0}
                    >
                      <ArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => moveSentence(index, 'down')}
                      disabled={index === formData.sentences.length - 1}
                    >
                      <ArrowDownIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => deleteSentence(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>

                <TextField
                  label="Speaker Name"
                  value={sentence.speakerName || ''}
                  onChange={(e) => updateSentence(index, 'speakerName', e.target.value)}
                  fullWidth
                  size="small"
                />

                <TextField
                  label="Text"
                  value={sentence.text || ''}
                  onChange={(e) => updateSentence(index, 'text', e.target.value)}
                  multiline
                  rows={3}
                  fullWidth
                  size="small"
                  helperText="Use <br> for line breaks, <color=#50BAAC>text</color> for colored text"
                />

                <TextField
                  label="Mobile Text Override (optional)"
                  value={sentence.textMobileOverride || ''}
                  onChange={(e) => updateSentence(index, 'textMobileOverride', e.target.value || null)}
                  multiline
                  rows={2}
                  fullWidth
                  size="small"
                />

                <Stack direction="row" spacing={2}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Speaker Sprite</InputLabel>
                    <Select
                      value={sentence.speakerSpriteName || ''}
                      onChange={(e) => updateSentence(index, 'speakerSpriteName', e.target.value)}
                      label="Speaker Sprite"
                    >
                      <MenuItem value="">
                        <em>None</em>
                      </MenuItem>
                      {availableSprites.length > 0 && (
                        <MenuItem disabled>
                          <em>— {formData.npcName} Sprites —</em>
                        </MenuItem>
                      )}
                      {availableSprites.map((sprite) => (
                        <MenuItem key={sprite} value={sprite}>
                          {sprite}
                        </MenuItem>
                      ))}
                      {availableSprites.length > 0 && allSprites.length > availableSprites.length && (
                        <MenuItem disabled>
                          <em>— All Other Sprites —</em>
                        </MenuItem>
                      )}
                      {allSprites
                        .filter(sprite => !availableSprites.includes(sprite))
                        .map((sprite) => (
                          <MenuItem key={sprite} value={sprite}>
                            {sprite}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Speaker Side</InputLabel>
                    <Select
                      value={sentence.speakerSide}
                      onChange={(e) => updateSentence(index, 'speakerSide', e.target.value)}
                      label="Speaker Side"
                    >
                      <MenuItem value={0}>Left (0)</MenuItem>
                      <MenuItem value={1}>Right (1)</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth size="small">
                    <InputLabel>Read Speed</InputLabel>
                    <Select
                      value={sentence.readSpeed || 'Default'}
                      onChange={(e) => updateSentence(index, 'readSpeed', e.target.value)}
                      label="Read Speed"
                    >
                      {READ_SPEEDS.map((speed) => (
                        <MenuItem key={speed} value={speed}>
                          {speed}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
            </Paper>
          ))}

          {error && (
            <Box sx={{ color: 'error.main', fontSize: '0.875rem' }}>
              {error}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConversationEditDialog;

